import {
  AuthErrorCode,
  LANGUAGE_HEADER,
  Language,
  isAuthTokenErrorCode,
} from "@repo/common"
import { AxiosInstance, InternalAxiosRequestConfig } from "axios"

// Extend the AxiosRequestConfig to include retry tracking
interface RetryableAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean
}

/** Why a session was ended against the user's will. Reported to Sentry. */
export type SessionLostReason =
  | "no-token-on-load"
  | "no-token-on-request"
  | "401-after-refresh"
  | "user-not-found"

interface AuthInterceptorDeps {
  /**
   * Returns a usable token, refreshing it if needed, or null if the session is
   * gone. Throws on transient failures, which must not end the session.
   */
  findIdToken: (forceRefresh?: boolean) => Promise<string | null>
  /**
   * Logs out and navigates to /login, preserving the current URL. Resolves to
   * whether a navigation actually started — when it did not, the caller must
   * surface the error rather than swallow it.
   */
  handleSessionLost: (reason: SessionLostReason) => Promise<boolean>
  /** Optional per-request store scoping header. */
  getStoreId?: () => Promise<string | null>
  /** The UI language, so the API returns DB-backed names (parts) in it. */
  getLanguage?: () => Language
}

interface StaleClientDeps {
  /** True when the running client code is older than the deployed version. */
  isClientStale: () => Promise<boolean>
}

/**
 * Resolves to nothing, ever. Used only after a navigation or reload has been
 * started — settling would surface an error screen for a page that is about to
 * be replaced anyway.
 */
const neverSettles = <T>(): Promise<T> => new Promise<T>(() => {})

/**
 * Requires a valid session for every request on this client.
 *
 * Attach this ONLY to clients that exclusively call `@Auth`-guarded endpoints.
 * The CRM's public endpoints (password reset) must use the interceptor-free
 * client in public-api.ts — routing them through here logs the user out before
 * the request is ever sent.
 *
 * Takes its dependencies as arguments rather than importing auth.ts, so both
 * the main client and the auth client can share these interceptors without a
 * circular import. The auth client previously had none at all, which left the
 * user-bootstrap call in the _auth route loader with no refresh and no retry.
 */
export const attachAuthInterceptors = (
  client: AxiosInstance,
  {
    findIdToken,
    handleSessionLost,
    getStoreId,
    getLanguage,
  }: AuthInterceptorDeps,
): AxiosInstance => {
  client.interceptors.request.use(
    async (config: RetryableAxiosRequestConfig) => {
      const idToken = await findIdToken()

      // Never send a request without a token: the API answers a bare request
      // with a generic 403 that is indistinguishable from a real permission
      // denial, which is what surfaced as "Något gick fel".
      if (!idToken) {
        const navigating = await handleSessionLost("no-token-on-request")
        if (navigating) return neverSettles<InternalAxiosRequestConfig>()
        throw new Error("Ingen giltig session")
      }

      config.headers.Authorization = `Bearer ${idToken}`

      if (getStoreId) {
        const storeId = await getStoreId()
        if (storeId) config.headers["Julo-Store-Id"] = storeId
      }

      if (getLanguage) config.headers[LANGUAGE_HEADER] = getLanguage()

      return config
    },
  )

  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest: RetryableAxiosRequestConfig | undefined =
        error.config

      const isUnauthorized = error.response?.status === 401
      const errorCode = error.response?.data?.errorCode

      // Only the auth guard's own token errors mean the token is missing or
      // stale — refresh once and replay. This is the silent recovery: the user
      // sees nothing but a slightly slower response. Any other 401 (a POS
      // system rejecting our credentials, say) says nothing about the session.
      if (
        isUnauthorized &&
        typeof errorCode === "string" &&
        isAuthTokenErrorCode(errorCode) &&
        originalRequest
      ) {
        if (!originalRequest._retry) {
          originalRequest._retry = true

          const newToken = await findIdToken(true)

          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`
            return client(originalRequest)
          }
        }

        // Either no token could be minted, or the replay failed with a freshly
        // minted one. The session is genuinely gone.
        if (await handleSessionLost("401-after-refresh")) return neverSettles()
        return Promise.reject(error)
      }

      // The token is genuine but its Julo user is gone; no refresh fixes that.
      if (isUnauthorized && errorCode === AuthErrorCode.USER_NOT_FOUND) {
        if (await handleSessionLost("user-not-found")) return neverSettles()
        return Promise.reject(error)
      }

      return Promise.reject(error)
    },
  )

  return client
}

/**
 * Reloads the page when a request fails and the running bundle turns out to be
 * older than the deployed one — a deploy can change routes and auth in ways
 * that make an old client's requests fail, and a reload beats an error screen.
 *
 * Attach this AFTER attachAuthInterceptors: axios runs response interceptors in
 * registration order, and the `>= 400` branch below would otherwise fire a
 * /version.json fetch on every expired token before the 401 refresh gets a
 * chance to recover silently.
 */
export const attachStaleClientReload = (
  client: AxiosInstance,
  { isClientStale }: StaleClientDeps,
): AxiosInstance => {
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      // Network errors with no response often mean the client is stale
      if (error.code === "ERR_NETWORK" && !error.response) {
        if (await isClientStale()) {
          console.info("[API] Network error with stale client, forcing reload")
          window.location.reload()
          return neverSettles()
        }
      }

      if (error.response && error.response.status >= 400) {
        if (await isClientStale()) {
          console.info("[API] HTTP error with stale client, forcing reload")
          window.location.reload()
          return neverSettles()
        }
      }

      return Promise.reject(error)
    },
  )

  return client
}
